import { useState, useEffect, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import axios from "axios";
import { adminApi } from "../../../data/axios/adminApi";
import { AdminPermission } from "../../../types/admin";

const PAGE_SIZE = 20;

export function usePermissionsAdmin() {
  const toast = useToast();
  const [permissions, setPermissions] = useState<AdminPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState<AdminPermission | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const fetchPermissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(false);
      const data = await adminApi.listPermissions({ search, page, page_size: PAGE_SIZE });
      setPermissions(data.permissions);
      setTotalPages(data.total_pages);
    } catch {
      setError(true);
      toast({ title: "Erro ao carregar permissões", status: "error", duration: 4000, isClosable: true });
    } finally {
      setLoading(false);
    }
  }, [search, page, toast]);

  useEffect(() => { setPage(1); }, [search]);

  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions]);

  const handleCreateOpen = () => {
    setSelectedPermission(null);
    setIsFormOpen(true);
  };

  const handleEditOpen = (perm: AdminPermission) => {
    setSelectedPermission(perm);
    setIsFormOpen(true);
  };

  const handleDeleteClick = (id: number) => setDeleteTargetId(id);
  const cancelDelete = () => setDeleteTargetId(null);

  const confirmDelete = async () => {
    if (deleteTargetId === null) return;
    try {
      await adminApi.deletePermission(deleteTargetId);
      toast({ title: "Permissão excluída com sucesso", status: "success", duration: 3000, isClosable: true });
      setDeleteTargetId(null);
      fetchPermissions();
    } catch (error) {
      const detail = axios.isAxiosError(error) ? error.response?.data?.detail : undefined;
      toast({ title: detail || "Erro ao excluir permissão", status: "error", duration: 5000, isClosable: true });
      setDeleteTargetId(null);
    }
  };

  return {
    permissions,
    loading,
    error,
    search,
    setSearch,
    page,
    totalPages,
    setPage,
    isFormOpen,
    setIsFormOpen,
    selectedPermission,
    deleteTargetId,
    handleCreateOpen,
    handleEditOpen,
    handleDeleteClick,
    confirmDelete,
    cancelDelete,
    refreshList: fetchPermissions,
  };
}
